import "@/index.css";
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { PageLoading, ErrorState } from "@/components/common";
import AppShell from "@/components/layout/AppShell";
import { AuthGateProvider } from "@/components/AuthGate";
import LaunchingSoon from "@/pages/LaunchingSoon";
import { LAUNCHING_SOON } from "@/config/launch";
import AuthCallback from "@/pages/AuthCallback";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import Post from "@/pages/Post";
import { Messages, Thread } from "@/pages/Messages";
import Profile from "@/pages/Profile";
import Saved from "@/pages/Saved";
import PublicProfile from "@/pages/PublicProfile";
import BusinessDetail from "@/pages/BusinessDetail";
import ListingDetail from "@/pages/ListingDetail";
import ServiceDetail from "@/pages/ServiceDetail";
import { Requests, RequestDetail } from "@/pages/Requests";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import { Safety, Legal } from "@/pages/Static";
import Verification from "@/pages/Verification";
import Settings from "@/pages/Settings";
import PostDetail from "@/pages/PostDetail";
import SupportMtaa from "@/pages/SupportMtaa";
import Deals from "@/pages/Deals";

function Protected({ children, requireOnboard = true }) {
  const { user, authError, refreshUser } = useAuth();
  const loc = useLocation();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (user !== null) { setSlow(false); return; }
    const t = setTimeout(() => setSlow(true), 15000); // safety net: never hang forever
    return () => clearTimeout(t);
  }, [user]);
  if (user === null) {
    if (authError || slow)
      return <ErrorState title="Couldn't connect" subtitle="Please check your connection and try again."
        onRetry={() => { setSlow(false); refreshUser(); }} />;
    return <PageLoading />;
  }
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (requireOnboard && !user.onboarded && loc.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  // TEMPORARY pre-launch gate: non-admins see the Launching Soon screen.
  // Admins (authenticated) bypass it. Auth routes stay reachable (no visible link)
  // so admins can sign in. Flip LAUNCHING_SOON to false to restore public browsing.
  const isAdmin = user && ["owner", "admin"].includes(user.role);
  if (LAUNCHING_SOON && !isAdmin) {
    if (user === null) return <PageLoading />;
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Protected requireOnboard={false}><Onboarding /></Protected>} />
        <Route path="*" element={<LaunchingSoon />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected requireOnboard={false}><Onboarding /></Protected>} />
      <Route path="/welcome" element={<Navigate to="/" replace />} />

      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/app" element={<Home />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/services" element={<Explore initialTab="services" />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/request/:id" element={<RequestDetail />} />
        <Route path="/business/:id" element={<BusinessDetail />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/p/:id" element={<PostDetail />} />
        <Route path="/service/:id" element={<ServiceDetail />} />
        <Route path="/u/:id" element={<PublicProfile />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />

        <Route path="/post" element={<Protected><Post /></Protected>} />
        <Route path="/post/:type" element={<Protected><Post /></Protected>} />
        <Route path="/messages" element={<Protected><Messages /></Protected>} />
        <Route path="/messages/:cid" element={<Protected><Thread /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/saved" element={<Protected><Saved /></Protected>} />
        <Route path="/verification" element={<Protected><Verification /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/support" element={<SupportMtaa />} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemeSync() {
  const { user } = useAuth();
  const { theme, adoptTheme } = useTheme();
  const done = useRef(false);
  useEffect(() => {
    if (user && user.theme_preference && !done.current) {
      done.current = true;
      if (user.theme_preference !== theme) adoptTheme(user.theme_preference);
    }
    if (user === false) done.current = false;
  }, [user, theme, adoptTheme]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ThemeSync />
        <BrowserRouter>
          <AuthGateProvider>
            <Toaster position="top-center" richColors theme="system" />
            <AppRouter />
          </AuthGateProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
